import type { IEventConfigRepository } from '@/domain/repositories/IEventConfigRepository';
import { createEventConfig } from '@/domain/entities/EventConfig';
import type { EventConfig } from '@/domain/entities/EventConfig';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import { API_BASE_URL } from '@/infrastructure/http/apiConfig';
import { DeviceSessionExpiredError } from '@/infrastructure/http/DeviceSessionExpiredError';
import { NetworkUnavailableError } from '@/infrastructure/http/NetworkUnavailableError';
import { parseJsonResponse } from '@/infrastructure/http/parseJsonResponse';

function buildConfigUrl(eventId: string): string {
  return `${API_BASE_URL}/events/${eventId}/config`;
}

async function fetchConfig(url: string, jwtToken: string): Promise<Response> {
  try {
    return await fetch(url, { headers: { Authorization: `Bearer ${jwtToken}` } });
  } catch (error) {
    // `fetch()` só rejeita (TypeError) quando a requisição não pôde nem ser
    // tentada — sem conectividade, DNS, CORS bloqueado etc. Sem cache válido
    // para a estratégia Network First recorrer, isso é "offline, aguardando
    // conexão" (Design da spec), nunca um erro genérico na tela (achado do
    // Tech Lead, review do PR #85).
    if (error instanceof TypeError) {
      throw new NetworkUnavailableError();
    }
    throw error;
  }
}

/**
 * Implementação que busca a configuração de ativação do evento chamando
 * `GET /events/{event_id}/config` com `Authorization: Bearer <jwtToken>`
 * (HUB-130/216). Substitui o antigo carregamento do arquivo estático
 * `/config/event.json` (HUB-235, CA-04) — o Service Worker intercepta esta
 * rota com a estratégia Network First + fallback ao cache (CA-05/CA-06).
 * Falha de rede pura (sem cache — `NetworkUnavailableError`) é distinguida
 * de erro HTTP/schema para a tela de carregamento saber qual estado exibir.
 *
 * A mesma resposta compõe `activation_config` e o texto de consentimento
 * aprovado do evento (`consent`, HUB-201/HUB-216) — uma única chamada de
 * rede para as duas necessidades, sem duplicar a requisição.
 */
export class HttpEventConfigRepository implements IEventConfigRepository {
  async load(session: DeviceSession): Promise<EventConfig> {
    const response = await fetchConfig(buildConfigUrl(session.eventId), session.jwtToken);

    if (response.status === 410) {
      throw new DeviceSessionExpiredError();
    }

    if (!response.ok) {
      throw new Error(`Falha ao carregar configuração do evento: HTTP ${response.status}`);
    }

    const data = await parseJsonResponse<unknown>(
      response,
      'Não foi possível carregar a configuração do evento. Tente novamente.'
    );
    return createEventConfig(data);
  }
}
